import { atom } from 'jotai';
import type { WellEurMetrics } from 'types/api-responses';

/**
 * EUR for the Insights forecast selected in the Production Details section.
 *
 * The EUR card and that section are separate sections built by the same hook,
 * so this atom passes the figures from one to the other. Null when no Insights
 * forecast is selected, and the card then uses the well's own EUR.
 */
export const selectedForecastEurAtom = atom<WellEurMetrics | null>(null);

/**
 * Converts a forecast's EUR totals into the shape the EUR card takes.
 *
 * The per-foot values are calculated here because the API only returns totals.
 * They are null without a lateral length to divide by.
 *
 * @param {object | null} metrics - The forecast's EUR totals.
 * @param {number | null | undefined} lateralLengthFt - The producing wellbore's perforated lateral.
 * @return {WellEurMetrics | null} The card's figures, or null without metrics.
 */
export const forecastEurMetrics = (
  metrics: {
    oilEur?: number | null;
    gasEur?: number | null;
    waterEur?: number | null;
  } | null,
  lateralLengthFt: number | null | undefined
): WellEurMetrics | null => {
  if (!metrics) return null;

  const perFoot = (value: number | null | undefined) => {
    if (value === null || value === undefined) return null;
    if (!lateralLengthFt) return null;

    return value / lateralLengthFt;
  };

  return {
    oilBbl: metrics.oilEur ?? null,
    gasMcf: metrics.gasEur ?? null,
    waterBbl: metrics.waterEur ?? null,
    oilBblPerFt: perFoot(metrics.oilEur),
    gasMcfPerFt: perFoot(metrics.gasEur),
    waterBblPerFt: perFoot(metrics.waterEur),
  };
};
